import React, { useEffect, useRef, useState } from "react";

import Slider from "react-slick";
import ModalVideo from "react-modal-video";
import Axios from "axios";
import Moment from "moment";

// Project type constants live at module scope so they are referentially stable
// and can be used safely inside effect dependency arrays.
const types = {
  IMAGE: "image",
  VIDEO: "video",
  DOCUMENT: "document",
  ILLUSTRATION: "Illustration",
};

// Tag IDs from the original bench capture.
const CAT_TAGS = {
  a12000168: "Buddy",
  a12000140: "Buzz",
};

const ProjectDetailsModal = ({ projectDetails, setIsOpen }) => {
  const sliderRef = useRef();

  const [RFIDList, taglist] = useState([]);

  // Fetch once when an image-type project opens. The dependency array is
  // required: without it this effect re-runs on every render caused by its own
  // setState, producing an unbounded request loop against the API.
  useEffect(() => {
    let cancelled = false;

    if (projectDetails?.type === types.IMAGE) {
      Axios.get("/api/getTags")
        .then((response) => {
          if (!cancelled) taglist(response.data);
        })
        .catch(() => {
          if (!cancelled) taglist([]);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [projectDetails]);

  // Derive each cat's last known location from the most recent read for its tag.
  const cats = { a12000168: { status: "downstairs" }, a12000140: { status: "downstairs" } };
  const curtime = new Moment();
  let latestRead = null;

  RFIDList.forEach((tag) => {
    const key = `a${tag.tagid}`;
    if (!cats[key]) return;
    const rfidtime = Moment(tag.tagtime);
    if (!latestRead || rfidtime.isAfter(latestRead)) latestRead = rfidtime;
    const timediff = curtime.diff(rfidtime, "minutes");
    cats[key].status = timediff <= 2 ? "upstairs" : "downstairs";
  });

  const SlickArrowLeft = ({ currentSlide, slideCount, ...props }) => (
    <button
      {...props}
      className={
        "slick-prev slick-arrow" + (currentSlide === 0 ? " slick-disabled" : "")
      }
      aria-hidden="true"
      aria-disabled={currentSlide === 0 ? true : false}
      type="button"
    >
      <i className="fa fa-chevron-left"></i>
    </button>
  );
  const SlickArrowRight = ({ currentSlide, slideCount, ...props }) => (
    <button
      {...props}
      className={
        "slick-next slick-arrow" +
        (currentSlide === slideCount - 1 ? " slick-disabled" : "")
      }
      aria-hidden="true"
      aria-disabled={currentSlide === slideCount - 1 ? true : false}
      type="button"
    >
      <i className="fa fa-chevron-right"></i>
    </button>
  );

  var settings = {
    dots: true,
    arrows: true,
    prevArrow: <SlickArrowLeft />,
    nextArrow: <SlickArrowRight />,
    infinite: true,
    adaptiveHeight: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4500,
    responsive: [
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          initialSlide: 1,
        },
      },
    ],
  };

  useEffect(() => {
    if (projectDetails?.type === types.DOCUMENT) {
      sliderRef.current.slickGoTo(0);
    }
  }, [projectDetails]);

  return (
    <>
      {projectDetails?.type === types.DOCUMENT && (
        <div className="project-details-modal">
          <div
            className="modal fade bg-dark-1 show"
            style={{ display: "block" }}
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => setIsOpen(false)}
          >
            <div
              className="modal-dialog modal-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className={"modal-content rounded-0"}>
                <div className="modal-body">
                  <button
                    type="button"
                    className={"btn-close "}
                    onClick={() => setIsOpen(false)}
                  />
                  <div className={"container ajax-container "}>
                    <h2 className={"text-6 fw-600 text-center mb-4"}>
                      {projectDetails?.title}
                    </h2>
                    <div className="row g-4">
                      <div className="col-md-7">
                        <Slider {...settings} ref={sliderRef}>
                          <div className="item">
                            <img
                              className="img-fluid"
                              alt=""
                              src={projectDetails?.thumbImage}
                            />
                          </div>
                          {projectDetails?.document?.sliderImages?.length > 0 &&
                            projectDetails?.document?.sliderImages?.map(
                              (image, index) => (
                                <div className="item" key={index}>
                                  <img
                                    className="img-fluid"
                                    alt=""
                                    src={image}
                                  />
                                </div>
                              )
                            )}
                        </Slider>
                      </div>
                      <div className="col-md-5">
                        <h4 className={"text-4 fw-600"}>Project Info:</h4>
                        <p>{projectDetails?.document?.projectInfo}</p>
                        <h4 className={"text-4 fw-600 mt-4"}>
                          Project Details:
                        </h4>
                        <ul className={"list-style-2 "}>
                          <li>
                            <span className={"text-dark fw-600 me-2"}>
                              Client:
                            </span>
                            {projectDetails?.document?.client}
                          </li>
                          <li>
                            <span className={"text-dark fw-600 me-2"}>
                              Technologies:
                            </span>
                            {projectDetails?.document?.technologies}
                          </li>
                          <li>
                            <span className={"text-dark fw-600 me-2"}>
                              Industry:
                            </span>
                            {projectDetails?.document?.industry}
                          </li>
                          <li>
                            <span className={"text-dark fw-600 me-2"}>
                              Date:
                            </span>
                            {projectDetails?.document?.date}
                          </li>

                          {projectDetails?.document?.url ? (
                            <li>
                              <span className={"text-dark fw-600 me-2"}>
                                URL:
                              </span>
                              <a
                                href={projectDetails?.document?.url?.link}
                                className="btn btn-primary shadow-none rounded-0 px-3 py-1"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {projectDetails?.document?.url?.name}
                                <i className="fas fa-external-link-alt ms-1" />
                              </a>
                            </li>
                          ) : (
                            ""
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {projectDetails?.type === types.IMAGE && (
        <div className="portfolio-image-modal" onClick={() => setIsOpen(false)}>
          <div className="img-wrap">
            <div id="cat1" className={"cattracker " + cats.a12000168.status}>
              <img src="images/projects/buddy-1-small.png" alt="buddy cat" />
              <span>{CAT_TAGS.a12000168}</span>
            </div>
            <div id="cat2" className={"cattracker " + cats.a12000140.status}>
              <img src="images/projects/buzz-1-small.png" alt="buzz cat" />
              <span>{CAT_TAGS.a12000140}</span>
            </div>
            <div className="img-meta img-title">{projectDetails?.title}</div>

            <div className="img-meta img-desc">
              {projectDetails?.description}
              <br />
              <small>
                Recorded capture &mdash; the RFID reader is offline. Tag reads
                are served live from MongoDB through a Node/Express API, but the
                readings themselves are from the original bench test
                {latestRead ? ` (last read ${latestRead.format("MMMM YYYY")})` : ""}
                , so both cats show their last known position.
              </small>
            </div>
            <img
              src={projectDetails?.thumbImage}
              alt={projectDetails?.title}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="overlay"></div>
            <button
              type="button"
              className="btn-close btn-close-white opacity-10"
              onClick={() => setIsOpen(false)}
            ></button>
          </div>
        </div>
      )}
      {projectDetails?.type === types.VIDEO && (
        <ModalVideo
          channel={projectDetails?.video?.vimeo ? "vimeo" : "youtube"}
          autoplay
          isOpen={projectDetails?.type === types.VIDEO}
          videoId={projectDetails?.video?.id}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default ProjectDetailsModal;
